import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from '../Modal';
import { Reminder, formatReminderDate } from '../../services/reminderService';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const QuickOptions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const QuickButton = styled.button`
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const CurrentInfo = styled.div`
  background: var(--background-color);
  padding: 12px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    
    &:hover {
      background: var(--primary-light); color: var(--primary-color);
    }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--hover-background);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

interface PostponeReminderModalProps {
  isOpen: boolean;
  reminder: Reminder;
  onClose: () => void;
  onPostpone: (newDate: string, reason?: string) => Promise<void>;
}

const PostponeReminderModal: React.FC<PostponeReminderModalProps> = ({
  isOpen,
  reminder,
  onClose,
  onPostpone
}) => {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setQuickOption = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    
    // Set to 9 AM by default
    date.setHours(9, 0, 0, 0);
    
    setNewDate(date.toISOString().split('T')[0]);
    setNewTime('09:00');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newDate || !newTime) {
      setError('נא לבחור תאריך ושעה');
      return;
    }

    const newDateTime = new Date(`${newDate}T${newTime}`);
    const now = new Date();

    if (newDateTime <= now) {
      setError('התאריך והשעה חייבים להיות בעתיד');
      return;
    }

    setLoading(true);
    try {
      await onPostpone(newDateTime.toISOString(), reason.trim() || undefined);
    } catch (err) {
      setError('שגיאה בדחיית התזכורת');
    } finally {
      setLoading(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // At least 30 minutes from now
    return {
      date: now.toISOString().split('T')[0],
      time: now.toTimeString().slice(0, 5)
    };
  };

  const minDateTime = getMinDateTime();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="דחיית תזכורת"
    >
      <Form onSubmit={handleSubmit}>
        <CurrentInfo>
          <strong>תזכורת נוכחית:</strong> {reminder.title}
          <br />
          <strong>זמן נוכחי:</strong> {formatReminderDate(reminder.dueDateTime)}
        </CurrentInfo>

        <FormGroup>
          <Label>תאריך חדש *</Label>
          <Input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            min={minDateTime.date}
            required
          />
          <QuickOptions>
            <QuickButton type="button" onClick={() => setQuickOption(1)}>
              מחר
            </QuickButton>
            <QuickButton type="button" onClick={() => setQuickOption(3)}>
              בעוד 3 ימים
            </QuickButton>
            <QuickButton type="button" onClick={() => setQuickOption(7)}>
              בעוד שבוע
            </QuickButton>
          </QuickOptions>
        </FormGroup>

        <FormGroup>
          <Label>שעה חדשה *</Label>
          <Input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>סיבת הדחייה</Label>
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="הסבר קצר לדחייה (אופציונלי)"
          />
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Actions>
          <Button type="button" onClick={onClose} disabled={loading}>
            ביטול
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'שומר...' : 'דחה תזכורת'}
          </Button>
        </Actions>
      </Form>
    </Modal>
  );
};

export default PostponeReminderModal;